import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { SellMarginPolicyListDataService } from './sell-margin-policy-list.data';
import { SellMarginPolicyListView } from './sell-margin-policy-list.view';
import { ContextService } from 'src/core/services/context.service';

interface Props extends ControllerProps<{}> {
}

interface State {
	mode: ControlMode;
}

@observer
export class SellMarginPolicyListController extends React.Component<Props, State> {
	@resolve
	private contextService: ContextService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: SellMarginPolicyListDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display'
		};
	}

	componentDidMount(): void {
		const { setBusy } = this.props;

		setBusy(true);
		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		this.dataService = SellMarginPolicyListDataService.make(this.contextService);

		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addSorting('priority', 'asc', true);
		this.dataService.pageSize = 20;

		await this.dataService.load((query) => query);
	}

	@autobind
	private handleModeChange(mode: ControlMode): void {
		this.setState({ mode });
	}

	@autobind
	private async handleSave(): Promise<void> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			await this.dataService.save();
			this.toast.showDataSaved();
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private swapDownPriorities(index: number): void {
		const [previous] = this.dataService.items.splice(index - 1, 1);
		this.dataService.items.splice(index, 0, previous);
		const previousPriority = this.dataService.items[index].priority;
		this.dataService.items[index].priority = this.dataService.items[index - 1].priority;
		this.dataService.items[index - 1].priority = previousPriority;
	}

	@autobind
	private resetPriorities(): void {
		for (let i = 0; i < this.dataService.items.length; i++) {
			this.dataService.items[i].priority = i + 1;
		}
	}

	@autobind
	private handleItemDoubleClick(item?: SellMarginPolicySummary): void {
		this.props.history.push(`/bookings/sell-margin-policies/${item == null ? 'new' : item.id}`);
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		return (
			<SellMarginPolicyListView
				canSave={this.contextService.isDirty}
				model={this.dataService}
				mode={this.state.mode}
				onItemDoubleClicked={this.handleItemDoubleClick}
				onModeChange={this.handleModeChange}
				onSave={this.handleSave}
				swapDownPriorities={this.swapDownPriorities}
				resetPriorities={this.resetPriorities}
				setBusy={this.props.setBusy}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/sell-margin-policies/list/sell-margin-policy-list.controller.tsx